import { useAdmin } from "./AdminContext";
import { Trash2, Upload, Plus, FolderOpen, Image, Scissors } from "lucide-react";

export function AdminCatalog() {
  const { catalogImages, catalogLoading, uploadingCatalog, catalogFileRef, handleUploadCatalog, handleDeleteCatalog, catalogCategories, newCatalogCatName, setNewCatalogCatName, handleAddCatalogCategory, handleDeleteCatalogCategory, toggleCatalogImageCategory, cutoutShapes, cutoutLoading, uploadingCutout, cutoutFileRef, handleUploadCutout, handleDeleteCutout } = useAdmin();

  return (
    <div>
      {/* Cutout Shapes Section */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-2xl font-black uppercase flex items-center gap-2"><Scissors size={22} strokeWidth={2.5} /> Cutout Shapes</h2>
            <p className="text-xs text-zinc-500 mt-1">Upload preset cutout shape images. These will appear as choices when a customer selects a cutout option.</p>
          </div>
          <div className="flex gap-3 items-center">
            <input ref={cutoutFileRef} type="file" accept="image/*" className="hidden" onChange={handleUploadCutout} />
            <button onClick={() => cutoutFileRef.current?.click()} disabled={uploadingCutout} className={`brutal-btn flex items-center gap-2 text-sm ${uploadingCutout ? "opacity-50" : ""}`} data-testid="upload-cutout-btn"><Upload size={16} strokeWidth={2.5} /> {uploadingCutout ? "Uploading..." : "Add Shape"}</button>
          </div>
        </div>
        {cutoutLoading ? (
          <div className="text-center py-8 text-zinc-400 font-heading uppercase tracking-widest animate-pulse">Loading...</div>
        ) : cutoutShapes.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-300 p-8 text-center" data-testid="no-cutout-shapes">
            <Scissors size={36} className="mx-auto mb-3 text-zinc-300" strokeWidth={2} />
            <h3 className="font-heading font-bold text-sm uppercase mb-2">No Cutout Shapes</h3>
            <p className="text-xs text-zinc-500">Upload shapes like circle, square, oval, etc.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="cutout-shapes-grid">
            {cutoutShapes.map(shape => (
              <div key={shape.id} className="brutal-card group" data-testid={`cutout-shape-${shape.id}`}>
                <div className="h-24 bg-zinc-100 border-b-2 border-black overflow-hidden">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${shape.storage_path}`} alt={shape.name} className="w-full h-full object-contain p-2" />
                </div>
                <div className="p-2 flex items-center justify-between">
                  <p className="text-[10px] text-zinc-500 font-mono truncate flex-1">{shape.name}</p>
                  <button onClick={() => handleDeleteCutout(shape.id)} className="text-red-500 hover:bg-red-50 p-1 border border-red-300 transition-colors" data-testid={`delete-cutout-${shape.id}`}><Trash2 size={12} strokeWidth={2.5} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Catalog Categories */}
      <div className="mb-8 border-2 border-black p-5 bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="catalog-categories-section">
        <div className="flex items-center gap-2 mb-4"><FolderOpen size={18} strokeWidth={2.5} className="text-[#FF3366]" /><h3 className="font-heading font-bold text-sm uppercase">Catalog Categories</h3></div>
        <div className="flex gap-2 mb-4">
          <input className="brutal-input flex-1 text-sm" placeholder="New category name..." value={newCatalogCatName} onChange={e => setNewCatalogCatName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAddCatalogCategory()} data-testid="new-catalog-cat-input" />
          <button onClick={handleAddCatalogCategory} disabled={!newCatalogCatName.trim()} className={`brutal-btn text-xs px-4 py-2 ${!newCatalogCatName.trim() ? "opacity-50" : ""}`} data-testid="add-catalog-cat-btn"><Plus size={14} className="inline mr-1" strokeWidth={2.5} /> Add</button>
        </div>
        {catalogCategories.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="catalog-categories-list">
            {catalogCategories.map(cat => (
              <div key={cat.id} className="flex items-center gap-1 bg-zinc-100 border-2 border-black px-3 py-1.5" data-testid={`catalog-cat-${cat.id}`}>
                <span className="text-xs font-heading font-bold uppercase">{cat.name}</span>
                <button onClick={() => handleDeleteCatalogCategory(cat.id)} className="text-red-500 hover:text-red-700 ml-1"><Trash2 size={12} strokeWidth={2.5} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Catalog Images */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-2xl font-black uppercase" data-testid="catalog-title">Catalog Images</h2>
          <p className="text-xs text-zinc-500 mt-1">Upload images customers can choose from during ordering.</p>
        </div>
        <div className="flex gap-3 items-center">
          <input ref={catalogFileRef} type="file" accept="image/*" className="hidden" onChange={handleUploadCatalog} />
          <button onClick={() => catalogFileRef.current?.click()} disabled={uploadingCatalog} className={`brutal-btn flex items-center gap-2 text-sm ${uploadingCatalog ? "opacity-50" : ""}`} data-testid="upload-catalog-btn"><Upload size={16} strokeWidth={2.5} /> {uploadingCatalog ? "Uploading..." : "Add Image"}</button>
        </div>
      </div>
      {catalogLoading ? (
        <div className="text-center py-12 text-zinc-400 font-heading uppercase tracking-widest animate-pulse">Loading...</div>
      ) : catalogImages.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 p-12 text-center" data-testid="no-catalog-images">
          <Image size={48} className="mx-auto mb-4 text-zinc-300" strokeWidth={2} /><h3 className="font-heading font-bold text-lg uppercase mb-2">No Catalog Images</h3>
          <p className="text-sm text-zinc-500">Upload images that customers can choose during customization.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="catalog-grid">
          {catalogImages.map(img => (
            <div key={img.id} className="brutal-card group" data-testid={`catalog-img-${img.id}`}>
              <div className="h-36 bg-zinc-100 border-b-2 border-black overflow-hidden"><img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${img.storage_path}`} alt={img.name} className="w-full h-full object-contain p-2" /></div>
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] text-zinc-500 font-mono truncate flex-1">{img.name}</p>
                  <button onClick={() => handleDeleteCatalog(img.id)} className="text-red-500 hover:bg-red-50 p-1 border border-red-300 transition-colors" data-testid={`delete-catalog-${img.id}`}><Trash2 size={12} strokeWidth={2.5} /></button>
                </div>
                {catalogCategories.length > 0 && (
                  <div className="space-y-1" data-testid={`catalog-img-cats-${img.id}`}>
                    {catalogCategories.map(cat => (
                      <label key={cat.id} className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={(img.category_ids || []).includes(cat.id)} onChange={() => toggleCatalogImageCategory(img.id, img.category_ids, cat.id)} className="w-3 h-3 border border-black accent-[#FF3366]" />
                        <span className="text-[9px] font-heading font-bold uppercase">{cat.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
